import Config from 'react-native-config';

import {createPromisedAction} from '/util/action.util';
import {fetchJSON} from '/util/fetch.util';
import {Logger} from '/util/splunk/logger.util';

import {getErrorHandler} from './apiError.action';

import {parseStreamingAbacusResponse} from '/service/abacus/streamParser';
import {AbacusConversationOptions} from '/types/abacus.types';

const ABACUS_ENV_MAP = {
  baseApiEndpoint: 'CHAT_BASE_API_ENDPOINT',
  deploymentToken: 'DEPLOYMENT_TOKEN',
  deploymentId: 'DEPLOYMENT_ID',
  apiKey: 'ABACUS_API_KEY',
} as const;

interface AbacusConfig {
  baseApiEndpoint: string;
  deploymentToken: string;
  deploymentId: string;
  apiKey?: string;
}

const readEnvValue = (name: string): string | undefined => {
  const rawValue = (Config as Record<string, unknown>)[name];

  if (typeof rawValue !== 'string') {
    return undefined;
  }

  const trimmed = rawValue.trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

const loadAbacusConfig = (): AbacusConfig => {
  const configuredBase =
    readEnvValue(ABACUS_ENV_MAP.baseApiEndpoint) ||
    'https://medifastinc.abacus.ai/api';
  const trimmedBase = configuredBase.replace(/\/+$/, '');
  const baseApiEndpoint = trimmedBase.endsWith('/api')
    ? trimmedBase
    : `${trimmedBase}/api`;
  const deploymentToken = readEnvValue(ABACUS_ENV_MAP.deploymentToken);
  const deploymentId = readEnvValue(ABACUS_ENV_MAP.deploymentId);
  const apiKey = readEnvValue(ABACUS_ENV_MAP.apiKey);

  const missing: string[] = [];
  if (!deploymentToken) {
    missing.push(`"${ABACUS_ENV_MAP.deploymentToken}"`);
  }
  if (!deploymentId) {
    missing.push(`"${ABACUS_ENV_MAP.deploymentId}"`);
  }

  if (missing.length > 0) {
    throw new Error(
      `Missing Abacus configuration values: ${missing.join(', ')}`,
    );
  }

  return {
    baseApiEndpoint,
    deploymentToken: deploymentToken!,
    deploymentId: deploymentId!,
    apiKey,
  };
};

const isMeaningfulString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const AGENT_STATUS_WORDS = new Set([
  'skipped',
  'pending',
  'processing',
  'queued',
  'cancelled',
  'canceled',
  'aborted',
  'error',
  'failed',
  'timeout',
  'null',
  'undefined',
]);

const normalizeAgentStatusToken = (value: string): string =>
  value
    .trim()
    .replace(/^['"]+|['"]+$/g, '')
    .replace(/^[[({\s]+/, '')
    .replace(/[\])}\s]+$/, '')
    .trim()
    .toLowerCase();

const isStatusOnlyResponse = (value: unknown): boolean =>
  isMeaningfulString(value) &&
  AGENT_STATUS_WORDS.has(normalizeAgentStatusToken(value));

const buildExecuteAgentUrl = (config: AbacusConfig): string => {
  const params = new URLSearchParams({
    deploymentToken: config.deploymentToken,
    deploymentId: config.deploymentId,
  });
  return `${config.baseApiEndpoint}/executeAgent?${params.toString()}`;
};

const buildExecuteAgentBody = (
  message: string,
  options?: AbacusConversationOptions,
): Record<string, unknown> => {
  const keywordArguments: Record<string, string> = {
    user_message: message,
  };

  const allowedMcn = options?.allowedMcn;
  if (isMeaningfulString(allowedMcn)) {
    keywordArguments.allowed_mcn = allowedMcn;
  }

  const emailId = options?.emailId;
  if (isMeaningfulString(emailId)) {
    keywordArguments.email_id = emailId;
  }

  const conversationId =
    (options?.conversationId ?? options?.deploymentConversationId) || null;
  if (isMeaningfulString(conversationId)) {
    keywordArguments.conversation_id = conversationId;
  }

  return {keywordArguments};
};

const executeAgentRequest = async (
  url: string,
  body: Record<string, unknown>,
): Promise<any> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      `executeAgent request failed with status ${response.status}`,
    );
  }

  return response.json();
};

const normalizeAbacusResponsePayload = (payload: any) => {
  if (!payload) {
    return payload;
  }

  if (typeof payload === 'string') {
    return parseStreamingAbacusResponse(payload);
  }

  return payload;
};

const extractSplunkResponseBody = (normalizedResponse: any) => {
  const payload = normalizedResponse?.payload ?? normalizedResponse;

  if (typeof payload !== 'string') {
    return payload;
  }

  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
};

export const GET_ABACUS_CONVERSATION = 'GET_ABACUS_CONVERSATION';
export const SET_ABACUS_LOADING = 'SET_ABACUS_LOADING';
export const SET_ABACUS_ERROR = 'SET_ABACUS_ERROR';
export const RESET_ABACUS_STATE = 'RESET_ABACUS_STATE';

export const setAbacusLoading = createPromisedAction(SET_ABACUS_LOADING, [
  'isLoading',
]);

export const setAbacusError = createPromisedAction(SET_ABACUS_ERROR, ['error']);

export const resetAbacusState = createPromisedAction(RESET_ABACUS_STATE, []);

export const getAbacusConversationResponse = createPromisedAction(
  GET_ABACUS_CONVERSATION,
  ['message', 'options'],
  async (dispatchMe, inputPayload, dispatch) => {
    const {message, options = {}} = inputPayload || {};

    if (
      !message ||
      typeof message !== 'string' ||
      message.trim().length === 0
    ) {
      throw new Error('Message is required to request an Abacus response.');
    }

    const config = loadAbacusConfig();
    const requestId = `abacus-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const timestamp = new Date().toISOString();
    const requestSentTime = new Date().toISOString();

    await dispatch(setAbacusLoading({isLoading: true}));

    try {
      const trimmedMessage = message.trim();
      const normalizedOptions = (options || {}) as AbacusConversationOptions;
      const {onStreamCompleted, ...persistableOptions} = normalizedOptions;
      const sanitizedOptionsCandidate = {...persistableOptions};
      delete sanitizedOptionsCandidate.onPartialResponse;
      delete sanitizedOptionsCandidate.onProgressResponse;
      delete sanitizedOptionsCandidate.chatConfig;
      const sanitizedOptions =
        Object.keys(sanitizedOptionsCandidate).length > 0
          ? (sanitizedOptionsCandidate as AbacusConversationOptions)
          : {};

      const executeAgentUrl = buildExecuteAgentUrl(config);
      const executeAgentBody = buildExecuteAgentBody(
        trimmedMessage,
        sanitizedOptions,
      );

      const response = await executeAgentRequest(
        executeAgentUrl,
        executeAgentBody,
      );
      const normalizedResponse = normalizeAbacusResponsePayload(response);

      const finalResponseText =
        typeof response === 'string' ? response : response?.response ?? null;

      if (
        isMeaningfulString(finalResponseText) &&
        !isStatusOnlyResponse(finalResponseText)
      ) {
        onStreamCompleted?.(finalResponseText);
      } else {
        onStreamCompleted?.(normalizedResponse);
      }

      const splunkResponseBody = extractSplunkResponseBody(normalizedResponse);
      const responseReceiveTime = new Date().toISOString();

      Logger.requestSplunkLog({
        logType: 'request',
        message: `Successfully received response for "${executeAgentUrl}"`,
        severity: 'LOW',
        requestData: {
          requestHeaders: {
            'Content-Type': 'application/json',
          },
          requestBody: executeAgentBody,
          timestamp: requestSentTime,
        },
        responseData: {
          responseBody: splunkResponseBody,
          statusCode: 200,
          responseReceiveTime,
        },
      });

      await dispatch(setAbacusError({error: null}));

      const action = await dispatchMe((payload: any) => ({
        ...payload,
        options: sanitizedOptions,
        response: normalizedResponse,
        requestId,
        timestamp,
      }));

      return action;
    } catch (error: any) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error(error?.message || 'Abacus request failed');
      const responseReceiveTime = new Date().toISOString();

      Logger.requestSplunkLog({
        logType: 'Error',
        message: 'Error response for "/executeAgent"',
        severity: 'HIGH',
        requestData: {
          requestHeaders: {
            'Content-Type': 'application/json',
          },
          requestBody: buildExecuteAgentBody(
            message.trim(),
            options as AbacusConversationOptions,
          ),
          timestamp: requestSentTime,
        },
        responseData: {
          responseBody:
            normalizedError.message || 'Abacus streaming request failed.',
          statusCode: 500,
          responseReceiveTime,
        },
      });

      await dispatch(
        setAbacusError({
          error:
            normalizedError.message ||
            'Unable to retrieve Abacus response at this time.',
        }),
      );

      getErrorHandler(dispatch, {
        promisedAction: getAbacusConversationResponse,
        params: inputPayload,
        shouldSignOut: false,
        sideAction: () => undefined,
      })(normalizedError);

      throw normalizedError;
    } finally {
      await dispatch(setAbacusLoading({isLoading: false}));
    }
  },
);

export interface DeploymentConversationMessage {
  role?: string | null;
  sender?: string | null;
  author?: string | null;
  type?: string | null;
  text?: string | null;
  message?: string | null;
  content?: string | null;
  response?: string | null;
  timestamp?: number | string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  messageIndex?: number;
  segments?: Array<Record<string, any>> | null;
}

export interface LastDataRefreshStatus {
  jobName?: string | null;
  lastSuccessfulRunTime?: string | null;
  runId?: string | null;
  executionTime?: number | null;
  message?: string | null;
}

export type DeploymentConversationFeedback = 'like' | 'dislike';

const buildGetDeploymentConversationUrl = (
  config: AbacusConfig,
  deploymentConversationId: string,
): string => {
  const params = new URLSearchParams({
    deploymentConversationId,
    deploymentToken: config.deploymentToken,
  });
  return `${
    config.baseApiEndpoint
  }/getDeploymentConversation?${params.toString()}`;
};

const buildSetDeploymentConversationFeedbackUrl = (
  config: AbacusConfig,
  deploymentConversationId: string,
  messageIndex: number,
): string => {
  const params = new URLSearchParams({
    deploymentConversationId,
    messageIndex: String(messageIndex),
    deploymentToken: config.deploymentToken,
  });
  return `${
    config.baseApiEndpoint
  }/setDeploymentConversationFeedback?${params.toString()}`;
};

export const fetchDeploymentConversationHistory = async (
  deploymentConversationId: string,
): Promise<DeploymentConversationMessage[]> => {
  const config = loadAbacusConfig();
  const url = buildGetDeploymentConversationUrl(
    config,
    deploymentConversationId,
  );

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (config.apiKey) {
    headers.apiKey = config.apiKey;
  }

  const response = await fetch(url, {method: 'GET', headers});
  if (!response.ok) {
    throw new Error(
      `getDeploymentConversation failed with status ${response.status}`,
    );
  }

  const data = await response.json();
  const payload = data?.data ?? data?.result ?? data?.response ?? data;
  const messages: unknown =
    payload?.deploymentConversation?.conversationMessages ??
    payload?.deploymentConversation?.messages ??
    payload?.deploymentConversation?.conversation?.conversationMessages ??
    payload?.deploymentConversation?.conversation?.messages ??
    payload?.history ??
    payload?.conversation?.conversationMessages ??
    payload?.conversation?.messages ??
    payload?.conversationMessages ??
    payload?.messages ??
    [];

  return Array.isArray(messages)
    ? (messages as DeploymentConversationMessage[])
    : [];
};

export const setDeploymentConversationFeedback = async (
  deploymentConversationId: string,
  messageIndex: number,
  feedback: DeploymentConversationFeedback,
): Promise<void> => {
  if (!isMeaningfulString(deploymentConversationId)) {
    throw new Error('deploymentConversationId is required for feedback.');
  }
  if (!Number.isFinite(messageIndex)) {
    throw new Error('messageIndex is required for feedback.');
  }

  const config = loadAbacusConfig();
  const url = buildSetDeploymentConversationFeedbackUrl(
    config,
    deploymentConversationId.trim(),
    messageIndex,
  );

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (config.apiKey) {
    headers.apiKey = config.apiKey;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      messageIndex,
      feedback,
      liked: feedback === 'like',
    }),
  });

  if (!response.ok) {
    let details = '';
    try {
      const payload = await response.json();
      if (typeof payload?.error === 'string' && payload.error.trim().length) {
        details = payload.error.trim();
      }
    } catch {
      // ignore body parse issues and fallback to status-only message
    }

    throw new Error(
      details
        ? `setDeploymentConversationFeedback failed with status ${response.status}: ${details}`
        : `setDeploymentConversationFeedback failed with status ${response.status}`,
    );
  }
};

export const fetchLastDataRefreshStatus =
  async (): Promise<LastDataRefreshStatus | null> => {
    const result = await fetchJSON('Monitoring/LastDataRefresh');
    if (result.error || result.meta.clientError || result.meta.serverError) {
      throw new Error(
        result.error || 'Monitoring/LastDataRefresh request failed.',
      );
    }

    const payload = result.data;

    if (!payload || typeof payload !== 'object') {
      return null;
    }

    return payload as LastDataRefreshStatus;
  };
